import type {
  FullDemandSignalDTO,
  FullSupplySignalDTO,
  MatchedSiteMaterialV2,
  PricedDemandSignalDTO,
  PricedSupplySignalDTO,
} from 'api';
import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';

import type { NetTakeRate } from '../types';

export type MatchedSiteMaterialItemContextType = {
  demandSignal: FullDemandSignalDTO;
  supplySignal?: FullSupplySignalDTO;
  siteMaterial: MatchedSiteMaterialV2;
  targetMargin?: number;
  setTotalNetTakeRate: (totalNetTakeRate: NetTakeRate | undefined) => void;
  totalNetTakeRate?: NetTakeRate;
  setTotalNetTakeRateLoading: (isLoading: boolean) => void;
  initialSellerOffer?: PricedDemandSignalDTO;
  initialBuyerOffer?: PricedSupplySignalDTO;
};

export const MatchedSiteMaterialItemContext = createContext<MatchedSiteMaterialItemContextType | undefined>(undefined);

export const useMatchedSiteMaterialItem = () => {
  const context = useContext(MatchedSiteMaterialItemContext);
  if (context === undefined) {
    throw new Error('useMatchedSiteMaterialItem must be used within a MatchedSiteMaterialItemContextProvider');
  }
  return context;
};

type ProviderProps = {
  demandSignal: FullDemandSignalDTO;
  supplySignal?: FullSupplySignalDTO;
  siteMaterial: MatchedSiteMaterialV2;
  initialSellerOffer?: PricedDemandSignalDTO;
  initialBuyerOffer?: PricedSupplySignalDTO;
  targetMargin?: number;
  children: ReactNode;
};

export const MatchedSiteMaterialItemContextProvider = ({
  children,
  siteMaterial,
  demandSignal,
  initialSellerOffer,
  initialBuyerOffer,
  supplySignal,
  targetMargin,
}: ProviderProps) => {
  const [totalNetTakeRate, setTotalNetTakeRate] = useState<NetTakeRate>();

  const memoizedSetTotalNetTakeRate = useCallback(setTotalNetTakeRate, [setTotalNetTakeRate]);

  const setTotalNetTakeRateLoading = useCallback(
    (isLoading: boolean) => {
      setTotalNetTakeRate((prev) => ({ ...prev, isLoading }));
    },
    [setTotalNetTakeRate]
  );

  return (
    <MatchedSiteMaterialItemContext.Provider
      value={{
        demandSignal,
        initialBuyerOffer,
        initialSellerOffer,
        setTotalNetTakeRate: memoizedSetTotalNetTakeRate,
        setTotalNetTakeRateLoading,
        siteMaterial,
        supplySignal,
        targetMargin,
        totalNetTakeRate,
      }}
    >
      {children}
    </MatchedSiteMaterialItemContext.Provider>
  );
};
